import { BaseCardWrapper } from '@healthhub/react-ui';
import { Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { IllustrationEServices } from '@shared/assets';
import { OutlinedRightArrowIcon } from '@shared/assets-icon';
import { MaxWidthContainer } from '@shared/components';
import { Category, CategoryType } from '@shared/constants/app-services';
import useIsAuthenticated from '@shared/hooks/useIsAuthenticated';
import { useGetAppServicesQuery, useGetPublicAppServicesQuery } from '@shared/services/systemSettings';

const { Text, Title } = Typography;

const PopularServices = () => {
  const { i18n, t } = useTranslation('home');
  const { isAuthenticated } = useIsAuthenticated();

  const getAppServicesBody = {
    Category: Category.PH,
    CategoryType: CategoryType.SERVICE_CATEGORY,
    Language: i18n.language,
  };
  const { data: appServicesData, isFetching: isFetchingAppServices } = useGetAppServicesQuery(getAppServicesBody, {
    skip: !isAuthenticated,
  });
  const { data: publicAppServicesData, isFetching: isFetchingPublicAppServices } = useGetPublicAppServicesQuery(
    getAppServicesBody,
    {
      skip: isAuthenticated,
    }
  );

  const data = appServicesData ?? publicAppServicesData;
  const isFetching = isFetchingAppServices || isFetchingPublicAppServices;

  return (
    <div className="flex w-full justify-center px-3 pb-5 pt-10 md:px-8 lg:px-18">
      <MaxWidthContainer>
        <div className="flex flex-col gap-2 lg:gap-6">
          <Title className="m-0 font-semibold text-primary-blue-grey-4" level={2}>
            {t('popularServices')}
          </Title>
          <div className="flex gap-5">
            <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2 md:gap-6 lg:w-8/12 lg:gap-x-6 lg:gap-y-8">
              {isFetching ? (
                <>
                  <Skeleton.Button className="size-full rounded-2xl" />
                  <Skeleton.Button className="size-full rounded-2xl" />
                  <Skeleton.Button className="size-full rounded-2xl" />
                  <Skeleton.Button className="size-full rounded-2xl" />
                </>
              ) : (
                data?.PopularServices.map((popularServices) => (
                  <Link key={popularServices.Code} to={popularServices.Action}>
                    <BaseCardWrapper className="h-full border border-secondary-green-100 p-4 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.12)] lg:p-6">
                      <div className="flex flex-col gap-4">
                        <div className="flex justify-between">
                          <Title className="m-0 flex-1 text-xl text-primary-blue-grey" level={5}>
                            {popularServices.Label}
                          </Title>
                          <div className="flex size-6 items-center justify-center rounded-full bg-primary-green-3">
                            <OutlinedRightArrowIcon className="[&_path]:stroke-white" />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <img alt="" className="size-14px" src={popularServices.Image} />
                          <Text className="text-sm font-normal text-functional-iron">
                            {popularServices.Description}
                          </Text>
                        </div>
                      </div>
                    </BaseCardWrapper>
                  </Link>
                ))
              )}
            </div>
            <div className="hidden w-4/12 self-center lg:flex">
              <img alt="" data-testid="illustration-eservices" src={IllustrationEServices} />
            </div>
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
};

export default PopularServices;
